import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ClockCounterClockwise, EnvelopeSimple, FilePdf, Eye, Palette } from "@phosphor-icons/react";

const ACTION_META = {
  generated: { label: "Généré", icon: FilePdf, color: "#F59E0B" },
  emailed: { label: "Envoyé par email", icon: EnvelopeSimple, color: "#10B981" },
  opened_by_client: { label: "Ouvert par le client", icon: Eye, color: "#8B5CF6" },
};

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function ReportHistoryList({ chantierId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/chantiers/${chantierId}/report-events`);
      setEvents(r.data);
    } catch (err) {
      console.debug("[report-history] load failed:", err?.message || err);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (!chantierId) return;
    load();
    const t = setInterval(load, 30_000); // auto-refresh
    return () => clearInterval(t);
  }, [chantierId]);

  const totalOpens = events.reduce((sum, e) => sum + (e.open_count || 0), 0);

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
      <button
        className="w-full flex items-center justify-between"
        onClick={() => setOpen((v) => !v)}
        data-testid="report-history-toggle"
      >
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={16} className="text-white/60" weight="duotone" />
          <div className="text-[11px] uppercase tracking-widest text-white/50">
            Historique des rapports ({events.length})
          </div>
        </div>
        {totalOpens > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-400 flex items-center gap-1">
            <Eye size={10} /> {totalOpens} ouverture{totalOpens > 1 ? "s" : ""}
          </span>
        )}
      </button>
      {open && (
        <div className="mt-3">
          {loading && <div className="text-xs text-white/40 py-3 text-center">Chargement…</div>}
          {!loading && events.length === 0 && (
            <div className="text-xs text-white/40 py-3 text-center">Aucun rapport généré pour l&apos;instant.</div>
          )}
          <div className="space-y-1.5">
            {events.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, icon: FilePdf, color: "#94A3B8" };
              const Ico = meta.icon;
              return (
                <div key={e.id} className="flex items-start gap-2 rounded-xl bg-white/[0.02] border border-white/5 p-2.5" data-testid={`report-event-${e.id}`}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.color + "22", color: meta.color }}>
                    <Ico size={14} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm">
                      <span className="font-medium">{meta.label}</span>
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{e.variant === "client" ? "client" : "terrain"}</span>
                    </div>
                    <div className="text-[11px] text-white/50 truncate">
                      {fmt(e.created_at)}
                      {e.actor_name && ` · par ${e.actor_name}`}
                      {e.recipient && ` · à ${e.recipient}`}
                      {e.report_template_name && (
                        <>
                          {" · "}
                          <span className="inline-flex items-center gap-1"><Palette size={9} /> {e.report_template_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {e.open_count > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-400 shrink-0 flex items-center gap-1">
                      <Eye size={9} /> {e.open_count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
